import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Paper,
  IconButton, Divider, List, ListItem, Grid, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent,
  Dialog, DialogTitle, DialogContent, DialogActions as MuiDialogActions, TextField, CircularProgress, Alert,
  RadioGroup, Radio, FormControlLabel, Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import { Producto } from '../../domain/entities/Producto';
import { CartItem, useCart } from '../../application/context/CartContext';
import { useSession } from '../../../../core/context/SessionContext';
import { VentaApiService, ClienteData, ClienteBusquedaResponse, UsoCFDI, extraerResumenPedidos } from '../../infrastructure/api/VentaApiService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { EnvioNacionalModal } from './EnvioNacionalModal/EnvioNacionalModal';
import { formDesdePayloadColombia } from './EnvioNacionalModal/EnvioNacionalModal.types';
import { calcularResumenImpuestosMX, calcularTotalesCO } from '../../application/utils/taxCalculator';
import { mensajeErrorFactura } from '../../application/utils/mensajeErrorFactura';

// Interfaz para los métodos de pago
interface MetodoPago {
  codigo: string;
  descripcion: string;
  habilitado: boolean;
  visible: boolean;
}

interface CarritoVentaPanelProps {
  emptyMessage?: string;
}

const clienteTicketGenerico = (pais: string) => {
  if (pais === 'CO') {
    return { nombre: 'Consumidor Final', documento: '222222222222', tipoDocumento: 'NIT' };
  }
  return { nombre: 'Público en General', documento: 'XAXX010101000', tipoDocumento: 'RFC' };
};

export const CarritoVentaPanel: React.FC<CarritoVentaPanelProps> = ({
  emptyMessage = 'Aún no hay productos en el carrito. Selecciona productos del catálogo para comenzar.',
}) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { carrito, actualizarCantidad, setCantidad, eliminarDelCarrito, limpiarCarrito } = useCart();
  const { terminal, equipo } = useSession();
  const [metodoPago, setMetodoPago] = useState<string>('');
  const [cantidadInput, setCantidadInput] = useState<Record<string, string>>({});

  const [isServicioModalOpen, setIsServicioModalOpen] = useState(false);
  const [editingEnvioItem, setEditingEnvioItem] = useState<CartItem | null>(null);

  const [metodosPago, setMetodosPago] = useState<MetodoPago[]>([]);

  // Estado para el modal de pago en efectivo
  const [isEfectivoModalOpen, setIsEfectivoModalOpen] = useState(false);
  const [montoRecibido, setMontoRecibido] = useState('');
  
  // Estado para el modal de facturación
  const [isFacturaModalOpen, setIsFacturaModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [isSearchingCliente, setIsSearchingCliente] = useState(false);
  const [busquedaRealizada, setBusquedaRealizada] = useState(false);
  const [mensajeBusquedaCliente, setMensajeBusquedaCliente] = useState<{
    tipo: 'success' | 'warning' | 'error';
    texto: string;
  } | null>(null);
  const [usosCFDI, setUsosCFDI] = useState<UsoCFDI[]>([]);
  const [isLoadingUsosCFDI, setIsLoadingUsosCFDI] = useState(false);
  const [errorCodigoPostal, setErrorCodigoPostal] = useState<string | null>(null);
  const [isLoadingCiudad, setIsLoadingCiudad] = useState(false);
  const [errorFacturacion, setErrorFacturacion] = useState<string | null>(null);
  const [modoFacturacion, setModoFacturacion] = useState<'consultar' | 'generico'>('consultar');
  const [departamentos, setDepartamentos] = useState<{ codigo: string; nombre: string }[]>([]);
  const [ciudades, setCiudades] = useState<string[]>([]);
  const [isLoadingDepartamentos, setIsLoadingDepartamentos] = useState(false);
  const [isLoadingCiudades, setIsLoadingCiudades] = useState(false);
  const departamentosCache = useRef<{ codigo: string; nombre: string }[] | null>(null);
  const ciudadesCacheRef = useRef<Record<string, string[]>>({});
  const [clienteData, setClienteData] = useState<ClienteData>({
    tipoDocumento: '',
    documento: '',
    nombre: '',
    correo: '',
    bpCliente: '',
    tipoPersona: '',
    usoCFDI: '',
    codigoPostal: '',
    ciudad: '',
    departamento: '',
    codigoCm: '',
    codigoPais: undefined,
    naturalezaReceptor: '',
    segundoNombre: '',
    primerApellido: '',
    segundoApellido: '',
  });
  
  // Determinar la moneda basada en el país de la terminal seleccionada
  const moneda: 'COP' | 'MXN' = terminal?.pais === 'MX' ? 'MXN' : 'COP'; 

  useEffect(() => {
    const cargarMetodos = async () => {
      if (!auth.user?.access_token) return;
      try {
        const metodosPagoBD = await VentaApiService.obtenerMetodosPagoBD(auth.user.access_token, terminal?.pais ?? '');
        setMetodosPago(metodosPagoBD || []);
      } catch {
        setMetodosPago([]);
      }
    };

    cargarMetodos();
  }, [auth.user?.access_token, terminal?.pais]);

  const getPrecio = (producto: Producto) => moneda === 'COP' ? producto.precioCOP : producto.precioMXN;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(moneda === 'COP' ? 'es-CO' : 'es-MX', {
      style: 'currency',
      currency: moneda,
      minimumFractionDigits: moneda === 'COP' ? 0 : 2
    }).format(amount);
  };

  const handleMetodoPagoChange = (event: SelectChangeEvent) => {
    setMetodoPago(event.target.value);
  };

  const handleAbrirModalFactura = () => {
    const metodoSeleccionado = metodosPago.find(m => m.codigo === metodoPago);
    const esEfectivo = metodoSeleccionado?.descripcion?.toLowerCase().includes('efectivo');
    if (esEfectivo) {
      setMontoRecibido('');
      setIsEfectivoModalOpen(true);
    } else {
      setIsFacturaModalOpen(true);
    }
    if (terminal?.pais === 'CO' && auth.user?.access_token) {
      if (departamentosCache.current) {
        setDepartamentos(departamentosCache.current);
      } else {
        setIsLoadingDepartamentos(true);
        VentaApiService.obtenerDepartamentosCO(auth.user.access_token)
          .then(data => {
            departamentosCache.current = data;
            setDepartamentos(data);
          })
          .catch(() => {})
          .finally(() => setIsLoadingDepartamentos(false));
      }
    }
  };

  const handleCerrarEfectivoModal = () => {
    setIsEfectivoModalOpen(false);
    setMontoRecibido('');
  };

  const handleProcesarEfectivo = () => {
    setIsEfectivoModalOpen(false);
    setIsFacturaModalOpen(true);
  };

  const handleCerrarModalFactura = () => {
    setIsFacturaModalOpen(false);
    setBusquedaRealizada(false);
    setMensajeBusquedaCliente(null);
    setUsosCFDI([]);
    setErrorCodigoPostal(null);
    setErrorFacturacion(null);
    setModoFacturacion('consultar');
    setClienteData({ tipoDocumento: '', documento: '', nombre: '', correo: '', bpCliente: '', tipoPersona: '', usoCFDI: '', codigoPostal: '', ciudad: '', departamento: '', codigoCm: '', codigoPais: undefined, naturalezaReceptor: '', segundoNombre: '', primerApellido: '', segundoApellido: '' });
  };

  const handleClienteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Si cambia el documento, reiniciar la búsqueda para que los campos vuelvan a ocultarse
    if (name === 'documento') {
      setBusquedaRealizada(false);
      setMensajeBusquedaCliente(null);
      setUsosCFDI([]);
      setErrorCodigoPostal(null);
      setErrorFacturacion(null);
      setClienteData(prev => ({
        ...prev,
        nombre: '',
        correo: '',
        bpCliente: '',
        tipoPersona: '',
        usoCFDI: '',
        codigoPostal: '',
        ciudad: '',
        departamento: '',
        codigoCm: '',
        codigoPais: undefined
      }));
    }
    
    // Si el campo es documento y el tipo seleccionado es RFC, forzar alfanumérico y máximo 13 caracteres
    if (name === 'documento' && clienteData.tipoDocumento === 'RFC') {
      const valorAlfanumerico = value.replaceAll(/[^a-zA-Z0-9]/g, '').toUpperCase();
      if (valorAlfanumerico.length <= 13) {
        setClienteData(prev => ({ ...prev, [name]: valorAlfanumerico }));
      }
    } else {
      setClienteData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validarFormularioFactura = (): boolean => {
    if (clienteData.tipoDocumento === 'RFC') {
      if (clienteData.documento.length < 12 || clienteData.documento.length > 13) {
        alert("El RFC debe tener entre 12 y 13 caracteres alfanuméricos.");
        return false;
      }
    }
    return true;
  };

  const isRFCValido = (): boolean => {
    if (clienteData.tipoDocumento !== 'RFC') return true;
    const len = clienteData.documento.length;
    return len >= 12 && len <= 13;
  };

  const isCodigoPostalValido = (): boolean => {
    if (clienteData.bpCliente !== '') return true;
    const cp = clienteData.codigoPostal ?? '';
    return cp.length >= 5 && cp.length <= 7 && !errorCodigoPostal && !!clienteData.ciudad;
  };

  const isCamposMXValidos = (): boolean => {
    if (terminal?.pais !== 'MX') return true;
    if (!clienteData.tipoPersona || !clienteData.usoCFDI) return false;
    return isCodigoPostalValido();
  };

  const isCamposCOCedulaValidos = (): boolean => {
    const { naturalezaReceptor, nombre, segundoNombre, primerApellido, segundoApellido, correo, departamento, ciudad } = clienteData;
    return !!(naturalezaReceptor && nombre && segundoNombre && primerApellido && segundoApellido && correo && departamento && ciudad);
  };

  const isCamposCONITValidos = (): boolean => {
    const { naturalezaReceptor, nombre, correo, departamento, ciudad } = clienteData;
    return !!(naturalezaReceptor && nombre && correo && departamento && ciudad);
  };

  const isFormularioValido = (): boolean => {
    if (modoFacturacion === 'generico') return true;
    const { tipoDocumento, documento, correo } = clienteData;
    if (!tipoDocumento || !documento) return false;
    if (terminal?.pais === 'CO' && tipoDocumento === 'CC' && busquedaRealizada) {
      return isCamposCOCedulaValidos();
    }
    if (terminal?.pais === 'CO' && tipoDocumento === 'NIT' && busquedaRealizada) {
      return isCamposCONITValidos();
    }
    if (!correo) return false;
    const { nombre } = clienteData;
    if (!nombre) return false;
    return isRFCValido() && isCamposMXValidos();
  };

  const handleTipoPersonaChange = async (valor: string) => {
    setClienteData(prev => ({ ...prev, tipoPersona: valor, usoCFDI: '' }));
    setUsosCFDI([]);

    if (!valor) return;

    setIsLoadingUsosCFDI(true);
    try {
      const lista = await VentaApiService.obtenerUsosCFDI(valor, auth.user?.access_token ?? '', terminal?.pais ?? '');
      setUsosCFDI(lista);
    } catch {
      setUsosCFDI([]);
    } finally {
      setIsLoadingUsosCFDI(false);
    }
  };

  const handleCodigoPostalChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = e.target.value.replaceAll(/\D/g, '').slice(0, 7);
    setClienteData(prev => ({ ...prev, codigoPostal: valor, ciudad: '', departamento: '', codigoCm: '', codigoPais: undefined }));
    setErrorCodigoPostal(null);

    if (valor.length === 5) {
      setIsLoadingCiudad(true);
      try {
        const resultado = await VentaApiService.consultarCodigoPostal(valor, auth.user?.access_token ?? '', terminal?.pais ?? '');
        const codigoPais = resultado.pais === 'Mexico' ? 484 : 170;
        setClienteData(prev => ({ ...prev, ciudad: resultado.ciudad, departamento: resultado.regionCodigo, codigoCm: resultado.codigoCm, codigoPais }));
      } catch {
        setErrorCodigoPostal('Código postal no válido');
      } finally {
        setIsLoadingCiudad(false);
      }
    }
  };

  const handleBuscarCliente = async () => {
    setIsSearchingCliente(true);
    setMensajeBusquedaCliente(null);

    try {
      const resultado: ClienteBusquedaResponse = await VentaApiService.buscarClientePorDocumento(
        clienteData.documento,
        auth.user?.access_token ?? '',
        terminal?.pais ?? ''
      );

      if (resultado.bpCliente === '') {
        setClienteData(prev => ({ ...prev, bpCliente: '', nombre: '' }));
        setMensajeBusquedaCliente({ tipo: 'warning', texto: 'Cliente no encontrado. Por favor ingresa los datos manualmente.' });
      } else {
        setClienteData(prev => ({ ...prev, bpCliente: resultado.bpCliente, nombre: resultado.nombre }));
        setMensajeBusquedaCliente({ tipo: 'success', texto: 'Cliente encontrado. Datos cargados automáticamente.' });
      }

      setBusquedaRealizada(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error desconocido';
      setMensajeBusquedaCliente({ tipo: 'error', texto: `Error al buscar el cliente: ${message}` });
    } finally {
      setIsSearchingCliente(false);
    }
  };

  const handleModoFacturacionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const modo = e.target.value as 'consultar' | 'generico';
    setModoFacturacion(modo);
    setBusquedaRealizada(false);
    setMensajeBusquedaCliente(null);
    setUsosCFDI([]);
    setErrorCodigoPostal(null);
    setErrorFacturacion(null);
    setClienteData({ tipoDocumento: '', documento: '', nombre: '', correo: '', bpCliente: '', tipoPersona: '', usoCFDI: '', codigoPostal: '', ciudad: '', departamento: '', codigoCm: '', codigoPais: undefined, naturalezaReceptor: '', segundoNombre: '', primerApellido: '', segundoApellido: '' });
  };

  const handleConfirmarFactura = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isSubmittingRef.current) return;

    if (!validarFormularioFactura()) {
      return;
    }
    
    if (!terminal || !equipo) {
      alert("Error: No hay una sesión de terminal activa.");
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      let clienteDataActualizado = { ...clienteData };

      if (modoFacturacion === 'consultar' && clienteData.bpCliente === '') {
        const bpCliente = await VentaApiService.crearClienteFacturacion(clienteData, auth.user?.access_token ?? '', terminal?.pais ?? '');
        clienteDataActualizado = { ...clienteData, bpCliente };
        setClienteData(clienteDataActualizado);
      }

      const resultadoSAP = await VentaApiService.crearFacturaSAP(
        terminal, equipo, carrito, metodoPago, clienteDataActualizado,
        auth.user?.access_token ?? ''
      );
      
      const metodoSeleccionado = metodosPago.find(m => m.codigo === metodoPago);

      const { salesOrder, codigosRemision, tieneEnvioNacional } = extraerResumenPedidos(resultadoSAP.data);

      const nuevaVenta = {
        idPedidoSAP: salesOrder,
        compraId: resultadoSAP.data.compraId,
        estadoCompra: resultadoSAP.data.estado,
        pedidos: resultadoSAP.data.pedidos,
        fecha: new Date().toISOString(),
        cliente: modoFacturacion === 'generico'
          ? clienteTicketGenerico(terminal.pais)
          : {
            nombre: clienteData.nombre,
            documento: clienteData.documento,
            tipoDocumento: clienteData.tipoDocumento,
          },
        items: carrito.map(item => ({
          descripcion: item.producto.descripcion,
          cantidad: item.cantidad,
          precioUnitario: getPrecio(item.producto),
          subtotal: getPrecio(item.producto) * item.cantidad
        })),
        subtotal: subtotal,
        impuestos: impuestos,
        total: total,
        ...(resumenMX && {
          baseGravable: resumenMX.baseGravable,
          ivaEnvios: resumenMX.ivaEnvios,
          ivaEmpaques: resumenMX.ivaEmpaques,
          totalImpuestos: resumenMX.totalImpuestos,
        }),
        metodoPago: metodoSeleccionado?.descripcion ?? metodoPago,
        moneda,
        terminal: terminal.nombre_terminal,
        equipo: equipo.codigo_equipo,
        tieneEnvioNacional,
        codigosRemision,
      };

      // El backend ahora se encarga de guardar en la base de datos automáticamente
      // al crear la factura, por lo que ya no llamamos a guardarHistorialBD aquí.

      limpiarCarrito();
      setMetodoPago('');
      setUsosCFDI([]);
      setErrorCodigoPostal(null);
      setErrorFacturacion(null);
      setModoFacturacion('consultar');
      setClienteData({ tipoDocumento: '', documento: '', nombre: '', correo: '', bpCliente: '', tipoPersona: '', usoCFDI: '', codigoPostal: '', ciudad: '', departamento: '', codigoCm: '', codigoPais: undefined, naturalezaReceptor: '', segundoNombre: '', primerApellido: '', segundoApellido: '' });
      setIsFacturaModalOpen(false);

      navigate('/venta/confirmacion', { state: { venta: nuevaVenta } });
    } catch (error) {
      const raw = error instanceof Error ? error.message : 'Error desconocido';
      setErrorFacturacion(mensajeErrorFactura(raw, carrito));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleDepartamentoChange = (codigoDpto: string) => {
    setClienteData(prev => ({ ...prev, departamento: codigoDpto, ciudad: '' }));
    setCiudades([]);
    if (!codigoDpto || !auth.user?.access_token) return;
    if (ciudadesCacheRef.current[codigoDpto]) {
      setCiudades(ciudadesCacheRef.current[codigoDpto]);
      return;
    }
    setIsLoadingCiudades(true);
    VentaApiService.obtenerCiudadesPorDepartamentoCO(codigoDpto, auth.user.access_token)
      .then(data => {
        const unique = Array.from(new Set(data));
        ciudadesCacheRef.current[codigoDpto] = unique;
        setCiudades(unique);
      })
      .catch(() => {})
      .finally(() => setIsLoadingCiudades(false));
  };

  const handleTipoDocumentoChange = (e: SelectChangeEvent) => {
    setClienteData(prev => ({ ...prev, tipoDocumento: e.target.value, documento: '', nombre: '', correo: '' }));
    setBusquedaRealizada(false);
    setMensajeBusquedaCliente(null);
  };

  const renderFormCO = () => {
    const isCC = clienteData.tipoDocumento === 'CC';
    return (
      <>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth required>
            <InputLabel>Naturaleza del receptor</InputLabel>
            <Select value={clienteData.naturalezaReceptor ?? ''} label="Naturaleza del receptor"
              onChange={(e) => setClienteData(prev => ({ ...prev, naturalezaReceptor: e.target.value }))}
            >
              <MenuItem value="Persona natural">Persona natural</MenuItem>
              <MenuItem value="Persona jurídica">Persona jurídica</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth required type="email" label="Correo electrónico" name="correo" value={clienteData.correo} onChange={handleClienteChange} />
        </Grid>
        {isCC ? (
          <>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required label="Nombre" name="nombre" value={clienteData.nombre} onChange={handleClienteChange}
                helperText={`${(clienteData.nombre ?? '').length}/60`} slotProps={{ htmlInput: { maxLength: 60 } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required label="Segundo nombre" name="segundoNombre" value={clienteData.segundoNombre ?? ''} onChange={handleClienteChange}
                helperText={`${(clienteData.segundoNombre ?? '').length}/60`} slotProps={{ htmlInput: { maxLength: 60 } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required label="Primer apellido" name="primerApellido" value={clienteData.primerApellido ?? ''} onChange={handleClienteChange}
                helperText={`${(clienteData.primerApellido ?? '').length}/60`} slotProps={{ htmlInput: { maxLength: 60 } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required label="Segundo apellido" name="segundoApellido" value={clienteData.segundoApellido ?? ''} onChange={handleClienteChange}
                helperText={`${(clienteData.segundoApellido ?? '').length}/60`} slotProps={{ htmlInput: { maxLength: 60 } }} />
            </Grid>
          </>
        ) : (
          <Grid size={{ xs: 12 }}>
            <TextField fullWidth required label="Razón social" name="nombre" value={clienteData.nombre} onChange={handleClienteChange}
              helperText={`${(clienteData.nombre ?? '').length}/60`}
              slotProps={{ htmlInput: { maxLength: 60 }, input: { readOnly: clienteData.bpCliente !== '' } }}
              sx={clienteData.bpCliente === '' ? {} : { '& .MuiInputBase-input': { color: 'text.disabled' } }}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete options={departamentos} getOptionLabel={(opt) => opt.nombre}
            value={departamentos.find(d => d.codigo === clienteData.departamento) ?? null}
            onChange={(_, newValue) => handleDepartamentoChange(newValue?.codigo ?? '')}
            disabled={isLoadingDepartamentos} loading={isLoadingDepartamentos} loadingText="Cargando..." noOptionsText="Sin resultados"
            renderInput={(params) => (
              <TextField {...params} required label="Departamento"
                placeholder={isCC ? undefined : '- Seleccionar departamento -'}
                slotProps={{ input: { ...params.InputProps, endAdornment: <>{isLoadingDepartamentos ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</> } }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete options={ciudades} getOptionLabel={(opt) => opt} value={clienteData.ciudad || null}
            onChange={(_, newValue) => setClienteData(prev => ({ ...prev, ciudad: newValue ?? '' }))}
            disabled={!clienteData.departamento || isLoadingCiudades} loading={isLoadingCiudades} loadingText="Cargando..." noOptionsText="Sin resultados"
            renderInput={(params) => (
              <TextField {...params} required label="Ciudad"
                placeholder={isCC ? undefined : '- Seleccionar ciudad -'}
                slotProps={{ input: { ...params.InputProps, endAdornment: <>{isLoadingCiudades ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</> } }}
              />
            )}
          />
        </Grid>
      </>
    );
  };

  const renderFormConsultar = () => (
    <>
      <Grid size={{ xs: 12 }}>
        <TextField fullWidth required
          label={['CE', 'PAS'].includes(clienteData.tipoDocumento) ? 'Nombre completo' : 'Nombre / Razón Social'}
          name="nombre" value={clienteData.nombre} onChange={handleClienteChange}
          slotProps={{ input: { readOnly: clienteData.bpCliente !== '' } }}
          sx={clienteData.bpCliente === '' ? {} : { '& .MuiInputBase-input': { color: 'text.disabled' } }}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField fullWidth required type="email" label="Correo Electrónico" name="correo" value={clienteData.correo} onChange={handleClienteChange} />
      </Grid>
      {terminal?.pais === 'MX' && (
        <>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              <InputLabel>Tipo Persona</InputLabel>
              <Select value={clienteData.tipoPersona ?? ''} label="Tipo Persona" onChange={(e) => handleTipoPersonaChange(e.target.value)}>
                <MenuItem value="Fisica">Física</MenuItem>
                <MenuItem value="Moral">Moral</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required disabled={!clienteData.tipoPersona || isLoadingUsosCFDI}>
              <InputLabel>{isLoadingUsosCFDI ? 'Cargando...' : 'Uso del CFDI'}</InputLabel>
              <Select value={clienteData.usoCFDI ?? ''} label={isLoadingUsosCFDI ? 'Cargando...' : 'Uso del CFDI'}
                onChange={(e) => setClienteData(prev => ({ ...prev, usoCFDI: e.target.value }))}
              >
                {usosCFDI.map((uso) => (
                  <MenuItem key={uso.id} value={uso.id}>{uso.id} — {uso.descripcion}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {clienteData.bpCliente === '' && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth required label="Código Postal" name="codigoPostal"
                  value={clienteData.codigoPostal ?? ''} onChange={handleCodigoPostalChange}
                  error={!!errorCodigoPostal} helperText={errorCodigoPostal ?? ''}
                  slotProps={{ htmlInput: { maxLength: 7 }, formHelperText: { sx: { color: 'error.main' } } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth required label="Ciudad" name="ciudad" value={clienteData.ciudad ?? ''}
                  slotProps={{ input: { readOnly: true, endAdornment: isLoadingCiudad ? <CircularProgress size={18} /> : null } }}
                />
              </Grid>
            </>
          )}
        </>
      )}
    </>
  );

  const renderModal = () => {
    let dialogTitle: string;
    if (terminal?.pais === 'CO') {
      dialogTitle = 'Facturación de cliente';
    } else if (busquedaRealizada) {
      dialogTitle = 'Datos de Facturación';
    } else {
      dialogTitle = 'Consulta de cliente y facturación';
    }

    const isCO = busquedaRealizada && terminal?.pais === 'CO' && (clienteData.tipoDocumento === 'CC' || clienteData.tipoDocumento === 'NIT');
    const isOtherConsulta = busquedaRealizada && !(terminal?.pais === 'CO' && (clienteData.tipoDocumento === 'CC' || clienteData.tipoDocumento === 'NIT'));

    return (
    <Dialog open={isFacturaModalOpen} onClose={handleCerrarModalFactura} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
        {dialogTitle}
      </DialogTitle>
      <form onSubmit={handleConfirmarFactura} aria-label="Formulario de facturación">
        <DialogContent dividers sx={{ pt: 3 }}>
          <RadioGroup row value={modoFacturacion} onChange={handleModoFacturacionChange} sx={{ mb: 2, gap: 2 }}>
            {terminal?.pais === 'CO' ? (
              <>
                <FormControlLabel value="consultar" control={<Radio />} label="Factura electrónica personalizada" />
                <FormControlLabel value="generico" control={<Radio />} label="Factura genérica" />
              </>
            ) : (
              <>
                <FormControlLabel value="consultar" control={<Radio />} label="Consultar cliente" />
                <FormControlLabel value="generico" control={<Radio />} label="Facturar a cliente genérico" />
              </>
            )}
          </RadioGroup>
          <Grid container spacing={3}>
            {modoFacturacion === 'consultar' && (
              <>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Tipo Doc.</InputLabel>
                    <Select name="tipoDocumento" value={clienteData.tipoDocumento} label="Tipo Doc." onChange={handleTipoDocumentoChange}>
                      {terminal?.pais === 'MX' ? (
                        [
                          <MenuItem key="RFC" value="RFC">RFC - Registro Federal de Contribuyentes</MenuItem>,
                          <MenuItem key="CURP" value="CURP">CURP - Clave Única de Registro de Población</MenuItem>
                        ]
                      ) : (
                        [
                          <MenuItem key="CC" value="CC">CC - Cédula de Ciudadanía</MenuItem>,
                          <MenuItem key="NIT" value="NIT">NIT - Número de Identificación Tributaria</MenuItem>,
                          <MenuItem key="CE" value="CE">CE - Cédula de Extranjería</MenuItem>,
                          <MenuItem key="PAS" value="PAS">Pasaporte</MenuItem>
                        ]
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth required label="Número de Documento" name="documento"
                      value={clienteData.documento} onChange={handleClienteChange}
                      slotProps={{ htmlInput: { maxLength: clienteData.tipoDocumento === 'RFC' ? 13 : undefined } }}
                      helperText={clienteData.tipoDocumento === 'RFC' ? `${clienteData.documento.length}/13 caracteres` : ''}
                      error={clienteData.tipoDocumento === 'RFC' && clienteData.documento.length > 0 && (clienteData.documento.length < 12 || clienteData.documento.length > 13)}
                    />
                    <Button variant="outlined" aria-label="buscar cliente" onClick={handleBuscarCliente}
                      disabled={!clienteData.tipoDocumento || !clienteData.documento || isSearchingCliente}
                      sx={{ height: '56px', minWidth: '56px', flexShrink: 0 }}
                    >
                      {isSearchingCliente ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    </Button>
                  </Box>
                </Grid>
                {mensajeBusquedaCliente && (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity={mensajeBusquedaCliente.tipo}>{mensajeBusquedaCliente.texto}</Alert>
                  </Grid>
                )}
                {isCO && renderFormCO()}
                {isOtherConsulta && renderFormConsultar()}
              </>
            )}
          </Grid>
        </DialogContent>
        <MuiDialogActions sx={{ p: 2, backgroundColor: '#f9f9f9', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
          {errorFacturacion && (
            <Alert severity="error" onClose={() => setErrorFacturacion(null)} sx={{ mb: 1 }}>
              {errorFacturacion}
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={handleCerrarModalFactura} color="inherit" sx={{ fontWeight: 'bold' }} disabled={isSubmitting}>
              Cancelar
            </Button>
            {(busquedaRealizada || modoFacturacion === 'generico') && (
              <Button type="submit" variant="contained" color="secondary"
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <ReceiptIcon />}
                sx={{ fontWeight: 'bold', borderRadius: 8, px: 3 }}
                disabled={isSubmitting || !isFormularioValido()}
              >
                {isSubmitting ? 'Procesando...' : 'Confirmar y Facturar'}
              </Button>
            )}
          </Box>
        </MuiDialogActions>
      </form>
    </Dialog>
    );
  };

  const totalItems = carrito.reduce((sum, item) => sum + item.cantidad, 0);

  const esItemEnvio = (item: typeof carrito[number]) =>
    Boolean(item.envioNacional || item.envioNacionalColombia);

  const taxableItems = carrito.map(item => ({
    precio: getPrecio(item.producto),
    cantidad: item.cantidad,
    esEnvio: esItemEnvio(item),
  }));

  const resumenMX = moneda === 'MXN' ? calcularResumenImpuestosMX(taxableItems) : null;
  const totalesCO = moneda === 'COP' ? calcularTotalesCO(taxableItems) : null;

  const subtotal = resumenMX ? resumenMX.baseGravable : (totalesCO?.subtotal ?? 0);
  const impuestos = resumenMX ? resumenMX.totalImpuestos : (totalesCO?.impuestos ?? 0);
  const total = resumenMX ? resumenMX.total : (totalesCO?.total ?? 0);

  return (
    <>
          <Paper
            elevation={3}
            data-testid="carrito-actual"
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {/* Header del Carrito */}
            <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Carrito Actual
              </Typography>
              <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', px: 1.5, py: 0.5, borderRadius: 4 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {totalItems} items
                </Typography>
              </Box>
            </Box>

            {/* Lista de Items */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }}>
              {carrito.length === 0 ? (
                <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography>{emptyMessage}</Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {(() => {
                    let envioCount = 0;
                    return carrito.map((item) => {
                      const esEnvio = esItemEnvio(item);
                      if (esEnvio) envioCount++;
                      const colonia = item.envioNacional?.destinatario.colonia;
                      const coloniaLabel = colonia ? ` (${colonia})` : '';
                      let envioLabel: string;
                      if (esEnvio) {
                        envioLabel = `Envío Nacional ${envioCount}${coloniaLabel}`;
                      } else {
                        envioLabel = item.producto.descripcion;
                      }
                      return { item, envioLabel, esEnvio };
                    });
                  })().map(({ item, envioLabel, esEnvio }) => (
                    <React.Fragment key={item.cartItemId}>
                      <ListItem sx={{ py: 2, px: 2, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', pr: 2 }}>
                            {envioLabel}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(getPrecio(item.producto) * item.cantidad)}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(getPrecio(item.producto))} c/u
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: 1, opacity: esEnvio ? 0.4 : 1 }}>
                            <IconButton size="small" aria-label="disminuir cantidad" disabled={esEnvio} onClick={() => actualizarCantidad(item.cartItemId, -1)} color="primary">
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <input
                              type="text"
                              inputMode="numeric"
                              disabled={esEnvio}
                              value={cantidadInput[item.cartItemId] ?? item.cantidad}
                              onChange={(e) => {
                                const soloNumeros = e.target.value.replaceAll(/\D/g, '');
                                setCantidadInput(prev => ({ ...prev, [item.cartItemId]: soloNumeros }));
                              }}
                              onKeyDown={(e) => {
                                if (['e', 'E', '-', '+', '.', ','].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onBlur={() => {
                                const val = Number.parseInt(cantidadInput[item.cartItemId] ?? '', 10);
                                if (!Number.isNaN(val) && val >= 1) {
                                  setCantidad(item.cartItemId, val);
                                }
                                setCantidadInput(prev => {
                                  const next = { ...prev };
                                  delete next[item.cartItemId];
                                  return next;
                                });
                              }}
                              style={{
                                width: '40px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                fontSize: '0.875rem',
                                border: 'none',
                                outline: 'none',
                                background: 'transparent',
                                padding: '0 4px',
                                cursor: esEnvio ? 'not-allowed' : 'text',
                              }}
                            />
                            <IconButton size="small" aria-label="aumentar cantidad" disabled={esEnvio} onClick={() => actualizarCantidad(item.cartItemId, 1)} color="primary">
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              aria-label="editar envío nacional"
                              color="primary"
                              onClick={() => { setEditingEnvioItem(item); setIsServicioModalOpen(true); }}
                              sx={{ visibility: esEnvio ? 'visible' : 'hidden' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" aria-label="eliminar del carrito" color="error" onClick={() => eliminarDelCarrito(item.cartItemId)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>

              )}
            </Box>

            {/* Totales y Botón de Facturar */}
            <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
              {resumenMX ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Base gravable</Typography>
                    <Typography variant="body2">{formatCurrency(resumenMX.baseGravable)}</Typography>
                  </Box>
                  {resumenMX.ivaEnvios > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">IVA Envíos (16%)</Typography>
                      <Typography variant="body2">{formatCurrency(resumenMX.ivaEnvios)}</Typography>
                    </Box>
                  )}
                  {resumenMX.ivaEmpaques > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">IVA Empaques (16%)</Typography>
                      <Typography variant="body2">{formatCurrency(resumenMX.ivaEmpaques)}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Total Impuestos</Typography>
                    <Typography variant="body2">{formatCurrency(resumenMX.totalImpuestos)}</Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                    <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Impuestos (19%)</Typography>
                    <Typography variant="body2">{formatCurrency(impuestos)}</Typography>
                  </Box>
                </>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Total</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {formatCurrency(total)}
                </Typography>
              </Box>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="metodo-pago-label">Método de Pago</InputLabel>
                <Select
                  labelId="metodo-pago-label"
                  value={metodoPago}
                  label="Método de Pago"
                  onChange={handleMetodoPagoChange}
                  disabled={carrito.length === 0}
                >
                  {metodosPago.map((metodo) => (
                    <MenuItem key={metodo.codigo} value={metodo.codigo}>
                      {metodo.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Button 
                variant="contained" 
                color="secondary" 
                fullWidth 
                size="large"
                disabled={carrito.length === 0 || metodoPago === ''}
                startIcon={<ReceiptIcon />}
                onClick={handleAbrirModalFactura}
                sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 'bold', borderRadius: 8 }}
              >
                Facturar
              </Button>
            </Box>
          </Paper>

      {/* Modal de Pago en Efectivo */}
      <Dialog open={isEfectivoModalOpen} onClose={handleCerrarEfectivoModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          Pago en Efectivo
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="body1">Total a cobrar</Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{formatCurrency(total)}</Typography>
          </Box>
          <TextField
            label="Monto recibido"
            type="number"
            fullWidth
            autoFocus
            value={montoRecibido}
            onChange={e => setMontoRecibido(e.target.value)}
            slotProps={{ htmlInput: { min: 0, step: 'any' } }}
            sx={{ mb: 3 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">Cambio a devolver</Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', color: Math.round(Number.parseFloat(montoRecibido || '0') * 100) >= Math.round(total * 100) ? 'primary.main' : 'error.main' }}
            >
              {formatCurrency(Math.max(0, Number.parseFloat(montoRecibido || '0') - total))}
            </Typography>
          </Box>
        </DialogContent>
        <MuiDialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
          <Button variant="outlined" onClick={handleCerrarEfectivoModal}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleProcesarEfectivo}
            disabled={Math.round(Number.parseFloat(montoRecibido || '0') * 100) < Math.round(total * 100)}
          >
            Procesar Pago
          </Button>
        </MuiDialogActions>
      </Dialog>

      {/* Modal de Datos de Facturación */}
      {renderModal()}

      {/* Modal de Envío Nacional */}
      <EnvioNacionalModal
        open={isServicioModalOpen}
        onClose={() => { setIsServicioModalOpen(false); setEditingEnvioItem(null); }}
        accessToken={auth.user?.access_token ?? ''}
        initialForm={
          editingEnvioItem?.envioNacional
          ?? editingEnvioItem?.envioNacionalForm
          ?? (editingEnvioItem?.envioNacionalColombia
            ? formDesdePayloadColombia(editingEnvioItem.envioNacionalColombia)
            : undefined)
        }
        editingItemId={editingEnvioItem?.cartItemId}
      />
    </>
  );
};
